// Language picker — flag dropdown synced to i18n + backend (when authenticated).
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import api from "@/api";
import { SUPPORTED_LANGS } from "@/i18n";

export default function LanguagePicker({ persistRemote = false, className = "" }) {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const current = SUPPORTED_LANGS.find((l) => l.code === i18n.language) || SUPPORTED_LANGS[0];

    const choose = async (code) => {
        i18n.changeLanguage(code);
        setOpen(false);
        if (persistRemote) {
            try { await api.put("/settings/language", { language: code }); } catch { /* ignore */ }
        }
    };

    return (
        <div ref={ref} className={`relative ${className}`} data-testid="language-picker">
            <button
                onClick={() => setOpen(!open)}
                data-testid="language-trigger"
                className="h-10 px-3 rounded-full border border-border bg-card flex items-center gap-1.5 text-sm font-semibold active:bg-secondary transition"
            >
                <span className="text-base leading-none">{current.flag}</span>
                <span className="hidden sm:inline">{current.label}</span>
                <ChevronDown size={14} className="opacity-60" />
            </button>
            {open && (
                <div className="absolute right-0 top-12 z-50 min-w-[180px] max-h-[60dvh] overflow-y-auto rounded-2xl border border-border bg-background shadow-2xl"
                     data-testid="language-menu">
                    {SUPPORTED_LANGS.map((l) => (
                        <button
                            key={l.code}
                            onClick={() => choose(l.code)}
                            data-testid={`language-option-${l.code}`}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-left active:bg-secondary transition ${l.code === current.code ? "bg-secondary" : ""}`}
                        >
                            <span className="text-lg leading-none">{l.flag}</span>
                            <span>{l.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
